import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Edit, Trash2, Shield, Filter, Building } from 'lucide-react';
import { useUsers } from 'features/administrativo/hooks/useUsers';
import UserForm from './UserForm';
import UserPermissions from './UserPermissions';
import UserCompanyManagement from './UserCompanyManagement';
import { getCompanyContext } from 'utils/setCompanyContext';

const UserManagement = () => {
  const { users, loading, deleteUser, loadUsers } = useUsers();
  
  // Função wrapper para loadUsers com log
  const handleLoadUsers = () => {
    loadUsers();
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPermissions, setShowPermissions] = useState(null);
  const [showCompanyManagement, setShowCompanyManagement] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [companyContext, setCompanyContext] = useState(() => getCompanyContext());

  useEffect(() => {
    const handleCompanyContextChanged = () => {
      setCompanyContext(getCompanyContext());
    };

    window.addEventListener('company-context-changed', handleCompanyContextChanged);
    return () => {
      window.removeEventListener('company-context-changed', handleCompanyContextChanged);
    };
  }, []);

  const selectedCompany = companyContext?.companies?.[0] || null;
  const isConsolidatedMode = Boolean(companyContext?.isGroup || companyContext?.isConsolidated);
  const selectedCompanyId = selectedCompany?.id;
  const selectedCompanyName = selectedCompany?.name ? String(selectedCompany.name).toLowerCase() : '';

  const userBelongsToSelectedCompany = useMemo(() => {
    return (user) => {
      if (isConsolidatedMode || !selectedCompanyId) return true;

      const userCompanyIds = Array.isArray(user.companies)
        ? user.companies
            .map((company) => (typeof company === 'object' ? company?.id : company))
            .filter((id) => id != null)
        : [];

      if (userCompanyIds.length > 0) {
        return userCompanyIds.includes(selectedCompanyId);
      }

      const userCompanyName = user.company ? String(user.company).toLowerCase() : '';
      return selectedCompanyName ? userCompanyName === selectedCompanyName : true;
    };
  }, [isConsolidatedMode, selectedCompanyId, selectedCompanyName]);

  // Filtrar usuários
  const filteredUsers = users.filter(user => {
    const matchesSelectedCompany = userBelongsToSelectedCompany(user);
    const matchesSearch = (user.full_name && String(user.full_name).toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (user.corporate_email && String(user.corporate_email).toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (user.company && String(user.company).toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = filterRole === 'all' || user.access_level === filterRole;
    
    return matchesSelectedCompany && matchesSearch && matchesRole;
  });

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Tem certeza que deseja excluir o usuário ${user.full_name}?`)) {
      try {
        await deleteUser(user.id);
      } catch (error) {
        console.error('Erro ao excluir usuário:', error);
      }
    }
  };

  const handlePermissions = (user) => {
    setShowPermissions(user);
  };

  const handleCompanyManagement = (user) => {
    setShowCompanyManagement(user);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingUser(null);
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <h3 className="text-lg font-medium mb-2 theme-text">Carregando usuários...</h3>
          <p className="text-sm theme-text-muted">Aguarde enquanto carregamos os dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold theme-text">Gerenciamento de Usuários</h2>
          <p className="text-sm sm:text-base theme-text-muted">Gerencie usuários e suas permissões</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span>Novo Usuário</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="p-3 sm:p-4 rounded-lg border theme-surface">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:gap-4">
          <div className="flex-1">
            <label htmlFor="user-search" className="sr-only">
              Buscar usuários
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted h-4 w-4" />
              <input
                id="user-search"
                type="text"
                placeholder="Buscar por nome, e-mail ou empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface"
                style={{ borderColor: 'var(--border)' }}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 theme-text-muted flex-shrink-0" />
            <label htmlFor="user-role-filter" className="sr-only">
              Filtrar por escopo
            </label>
            <select
              id="user-role-filter"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface"
              style={{ borderColor: 'var(--border)' }}
            >
              <option value="all">Todos os escopos</option>
              <option value="group_manager">Acesso por Grupo</option>
              <option value="company_manager">Acesso por Empresa</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Usuários */}
      <div className="rounded-lg border overflow-hidden theme-surface">
        {/* Desktop - Tabela */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
            <thead className="theme-surface-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Escopo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Permissões
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium theme-text">
                        {user.full_name}
                      </div>
                      <div className="text-sm theme-text-muted">
                        {user.corporate_email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm theme-text">
                      {user.company || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.access_level === 'group_manager' 
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.access_level === 'group_manager' ? 'Acesso por Grupo' : 'Acesso por Empresa'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Shield className="h-4 w-4 theme-text-muted" />
                      <span className="text-sm theme-text">
                        {user.permissions_count || 0} permissões
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleCompanyManagement(user)}
                        aria-label={`Gerenciar empresas de ${user.full_name}`}
                        className="text-green-600 hover:text-green-900 h-11 w-11 inline-flex items-center justify-center rounded hover:bg-green-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                        title="Gerenciar Empresas"
                      >
                        <Building className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handlePermissions(user)}
                        aria-label={`Gerenciar permissões de ${user.full_name}`}
                        className="text-blue-600 hover:text-blue-900 h-11 w-11 inline-flex items-center justify-center rounded hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        title="Gerenciar Permissões"
                      >
                        <Shield className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(user)}
                        aria-label={`Editar usuário ${user.full_name}`}
                        className="theme-text-muted hover:opacity-80 h-11 w-11 inline-flex items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        title="Editar Usuário"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        aria-label={`Excluir usuário ${user.full_name}`}
                        className="text-red-600 hover:text-red-900 h-11 w-11 inline-flex items-center justify-center rounded hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                        title="Excluir Usuário"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile - Cards */}
        <div className="lg:hidden">
          {filteredUsers.map((user) => (
            <div key={user.id} className="border-b p-4" style={{ borderColor: 'var(--border)' }}>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {user.full_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium theme-text leading-5 break-words">
                      {user.full_name}
                    </h3>
                    <p className="text-xs theme-text-muted break-all">
                      {user.corporate_email}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs theme-text-muted">Empresa:</span>
                    <span className="text-xs theme-text text-right break-words">{user.company || 'N/A'}</span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs theme-text-muted">Escopo:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.access_level === 'group_manager'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.access_level === 'group_manager' ? 'Acesso por Grupo' : 'Acesso por Empresa'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs theme-text-muted">Permissões:</span>
                    <div className="flex items-center space-x-1">
                      <Shield className="h-3 w-3 theme-text-muted" />
                      <span className="text-xs theme-text">
                        {user.permissions_count || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleCompanyManagement(user)}
                    aria-label={`Gerenciar empresas de ${user.full_name}`}
                    className="text-green-600 hover:text-green-900 h-11 px-3 inline-flex items-center justify-center gap-2 rounded border focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                    style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                    title="Gerenciar Empresas"
                  >
                    <Building className="h-4 w-4" />
                    <span className="text-xs font-medium">Empresas</span>
                  </button>
                  <button
                    onClick={() => handlePermissions(user)}
                    aria-label={`Gerenciar permissões de ${user.full_name}`}
                    className="text-blue-600 hover:text-blue-900 h-11 px-3 inline-flex items-center justify-center gap-2 rounded border focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                    title="Gerenciar Permissões"
                  >
                    <Shield className="h-4 w-4" />
                    <span className="text-xs font-medium">Permissões</span>
                  </button>
                  <button
                    onClick={() => handleEdit(user)}
                    aria-label={`Editar usuário ${user.full_name}`}
                    className="theme-text-muted hover:opacity-80 h-11 px-3 inline-flex items-center justify-center gap-2 rounded border focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                    title="Editar Usuário"
                  >
                    <Edit className="h-4 w-4" />
                    <span className="text-xs font-medium">Editar</span>
                  </button>
                  <button
                    onClick={() => handleDelete(user)}
                    aria-label={`Excluir usuário ${user.full_name}`}
                    className="text-red-600 hover:text-red-900 h-11 px-3 inline-flex items-center justify-center gap-2 rounded border focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                    style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                    title="Excluir Usuário"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="text-xs font-medium">Excluir</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="theme-text">
              {searchTerm || filterRole !== 'all' 
                ? 'Nenhum usuário encontrado com os filtros aplicados'
                : 'Nenhum usuário cadastrado'
              }
            </div>
            {!searchTerm && filterRole === 'all' && (
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Criar Primeiro Usuário
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modais */}
      {showForm && (
        <UserForm
          user={editingUser}
          onClose={handleCloseForm}
          onUpdate={handleLoadUsers}
        />
      )}

      {showPermissions && (
        <UserPermissions
          user={showPermissions}
          onClose={() => setShowPermissions(null)}
          onUpdate={handleLoadUsers}
        />
      )}

      {showCompanyManagement && (
        <UserCompanyManagement
          user={showCompanyManagement}
          onClose={() => setShowCompanyManagement(null)}
          onUpdate={handleLoadUsers}
        />
      )}
    </div>
  );
};

export default UserManagement;
